import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, Clock, User, Phone, Scissors } from 'lucide-react';
import api from '../api/axios';
import { useToast } from './Toast';
import { filterPhone } from '../utils/inputValidation';
import { getPlaceholders } from '../utils/placeholders';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const timeToMinutes = (t) => {
  if (!t) return 0;
  const sep = t.includes('h') ? 'h' : ':';
  const [h, m] = t.split(sep).map(Number);
  return h * 60 + (m || 0);
};

const minutesToTime = (total) => {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
};

const SLOT_INTERVAL = 30;
const DAY_START_MIN = 7 * 60;
const DAY_END_MIN   = 21 * 60;

const ALL_SLOTS = (() => {
  const slots = [];
  for (let m = DAY_START_MIN; m < DAY_END_MIN; m += SLOT_INTERVAL) {
    slots.push(minutesToTime(m));
  }
  return slots;
})();

const DURATIONS = [
  { label: '15 min', value: 15  },
  { label: '30 min', value: 30  },
  { label: '45 min', value: 45  },
  { label: '1 h',    value: 60  },
  { label: '1h30',   value: 90  },
  { label: '2 h',    value: 120 },
  { label: '2h30',   value: 150 },
  { label: '3 h',    value: 180 },
  { label: '3h30',   value: 210 },
  { label: '4 h',    value: 240 },
  { label: '4h30',   value: 270 },
  { label: '5 h',    value: 300 },
  { label: '5h30',   value: 330 },
  { label: '6 h',    value: 360 },
];

// ─── Composant ────────────────────────────────────────────────────────────────
export default function BookingEditModal({ booking, employees, salonDevise, onClose, onSaved }) {
  const showToast = useToast();
  const [saving, setSaving] = useState(false);

  // Calculer la durée initiale depuis heureDebut / heureFin
  const initialDuration = booking
    ? timeToMinutes(booking.heureFin) - timeToMinutes(booking.heureDebut)
    : 60;

  const [form, setForm] = useState({
    date:            booking ? new Date(booking.date).toISOString().split('T')[0] : '',
    heureDebut:      booking?.heureDebut || '09h00',
    duration:        initialDuration > 0 ? initialDuration : 60,
    clientNom:       booking?.clientNom || '',
    clientTelephone: booking?.clientTelephone || '',
    employeeId:      booking?.employeeId?._id || booking?.employeeId || '',
    service:         booking?.service || '',
  });

  // Fermer avec Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const set = (field) => (e) => {
    const v = field === 'clientTelephone' ? filterPhone(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientNom.trim()) {
      showToast('Le nom du client est obligatoire', 'error');
      return;
    }

    const startMin = timeToMinutes(form.heureDebut);
    const endMin   = startMin + Number(form.duration);
    const heureFin = minutesToTime(endMin);

    setSaving(true);
    try {
      const payload = {
        date:            form.date,
        heureDebut:      form.heureDebut,
        heureFin,
        clientNom:       form.clientNom.trim(),
        clientTelephone: form.clientTelephone.trim(),
        employeeId:      form.employeeId || null,
        service:         form.service.trim(),
      };
      await api.put(`/admin/bookings/${booking._id}`, payload);
      showToast('RDV modifié avec succès', 'success');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de la modification', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-end sm:items-center justify-center z-[100] p-0 sm:p-4">
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-lg shadow-2xl relative max-h-[92vh] flex flex-col">

        {/* ── En-tête ── */}
        <div className="p-6 pb-4 border-b border-slate-100 shrink-0 flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-900">Modifier le RDV</h2>
          <button onClick={onClose} className="text-slate-300 hover:text-slate-600 transition-colors p-1">
            <X size={22} />
          </button>
        </div>

        {/* ── Formulaire ── */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Date */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              <Calendar size={12} className="inline mr-1" /> Date
            </label>
            <input
              type="date"
              value={form.date}
              onChange={set('date')}
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Heure + Durée */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
                <Clock size={12} className="inline mr-1" /> Heure de début
              </label>
              <select
                value={form.heureDebut}
                onChange={set('heureDebut')}
                className="w-full border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {ALL_SLOTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
                <Clock size={12} className="inline mr-1" /> Durée
              </label>
              <select
                value={form.duration}
                onChange={set('duration')}
                className="w-full border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {DURATIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Nom client */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              <User size={12} className="inline mr-1" /> Nom du client
            </label>
            <input
              type="text"
              value={form.clientNom}
              onChange={set('clientNom')}
              placeholder="Prénom Nom"
              required
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Téléphone client */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              <Phone size={12} className="inline mr-1" /> Téléphone (optionnel)
            </label>
            <input
              type="tel"
              value={form.clientTelephone}
              onChange={set('clientTelephone')}
              placeholder={getPlaceholders(salonDevise).telephone}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Coiffeur */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              <User size={12} className="inline mr-1" /> Coiffeur assigné
            </label>
            <select
              value={form.employeeId}
              onChange={set('employeeId')}
              className="w-full border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">- Non assigné -</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.prenom} {emp.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Service */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              <Scissors size={12} className="inline mr-1" /> Prestation (optionnel)
            </label>
            <input
              type="text"
              value={form.service}
              onChange={set('service')}
              placeholder="Ex : Coupe + barbe"
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </form>

        {/* ── Footer ── */}
        <div className="p-6 pt-4 border-t border-slate-100 shrink-0">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-2xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-60"
            >
              <Save size={16} />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
